import { Component, EventEmitter, Input, Output, OnInit, OnChanges } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { NavItem, NavSection } from '../../models/layout.model';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.css',
  standalone: false,
})
export class SidebarComponent implements OnInit, OnChanges {
  @Input() sections: NavSection[] = [];
  @Input() menuOpen = false;

  @Output() closeMenu = new EventEmitter<void>();

  expandedItems: Set<string> = new Set();
  isCollapsed = false;

  constructor(private router: Router) {}

  ngOnInit(): void {
    // Load collapsed state from localStorage
    const savedCollapsedState = localStorage.getItem('sidebar-collapsed');
    if (savedCollapsedState !== null) {
      this.isCollapsed = savedCollapsedState === 'true';
    }

    // Auto-expand parent items based on current route
    this.updateExpandedItems();

    // Watch for route changes to auto-expand parent items
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateExpandedItems();
      });
  }

  ngOnChanges(): void {
    // Update expanded items when sections change
    this.updateExpandedItems();
  }

  toggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
    localStorage.setItem('sidebar-collapsed', String(this.isCollapsed));
  }

  private updateExpandedItems(): void {
    const currentUrl = this.router.url;
    
    // Check each parent item to see if any of its children match the current route
    this.sections.flatMap(section => section.items).forEach(item => {
      if (item.children && item.children.length > 0) {
        const hasActiveChild = item.children.some(child => {
          // Use exact matching to prevent false positives
          // (e.g., /organization should not match /organization/services)
          if (!child.route) return false;
          
          // Extract the base path without query params or fragments
          const baseUrl = currentUrl.split('?')[0].split('#')[0];
          const baseRoute = child.route.split('?')[0].split('#')[0];
          
          return baseUrl === baseRoute;
        });
        
        if (hasActiveChild) {
          this.expandedItems.add(item.label);
        }
      }
    });
  }

  toggleExpand(label: string, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.expandedItems.has(label)) {
      this.expandedItems.delete(label);
    } else {
      this.expandedItems.add(label);
    }
  }

  isExpanded(label: string): boolean {
    return this.expandedItems.has(label);
  }

  hasActiveChild(item: NavItem): boolean {
    if (!item.children || item.children.length === 0) {
      return false;
    }
    
    const currentUrl = this.router.url;
    return item.children.some(child => {
      if (!child.route) return false;
      
      // Use exact matching to prevent false positives
      const baseUrl = currentUrl.split('?')[0].split('#')[0];
      const baseRoute = child.route.split('?')[0].split('#')[0];
      
      return baseUrl === baseRoute;
    });
  }
}
